import $ from 'jquery';
import { XhrError } from '../../remote/request.js';
import { createContentIframe } from '../../util/iframe.js';

/**
 * Return an error markup for the given message
 *
 * @param {string} message
 */
export const getErrorMarkup = (message) => {
	return mpro.config.ui.alert.markup.replace('%message%', message);
};

/**
 * Return the generic error markup
 */
export const getGenericErrorMarkup = (message) => {
	return $(getErrorMarkup(getGenericErrorMessage()));
};

export const getGenericErrorMessage = () => {
	return mpro.langvar('error_unknown');
};

/**
 * @param {{ url: string; method?: string; data?: any; type?: any; }} initRequest
 * @param {XhrError|Error} error
 */
export const getErrorHtml = function(initRequest, error) {

	// Show details only on DEV
	if (mpro.isDev === true) {

		let title;
		let status;
		let details;

		if (error instanceof XhrError) {

			let $response;
			title = 'Something went wrong on the backend';

			// Try adding the content inside an iframe
			try {
				const iframe = createContentIframe(error.responseText);
				iframe.dataset.iframeId = 'mp-error-iframe';
				$response = $('<div></div>').append(iframe);
			}
			// Security error (CSP most likely)
			catch (error_) {

				// Log error
				console.warn(error_);

				// Parse the response and keep only most of what we need
				$response = $(error.responseText);
				// Wrap everything in a div
				$response = $response.wrapAll('<div></div>').parent();
				$response.find('title').remove();
				$response.find('base').remove();
				$response.find('meta').remove();
				$response.find('link').remove();
				$response.find('style').filter(function() {
					return !this.textContent.includes('shm-debug');
				}).remove();
				$response.find('script').filter(function() {
					return !this.textContent.includes('__SHMDBG__');
				}).remove();

				// Remove custom content found in a 404 page
				// $response.find('.wrap').remove();
			}

			status = `${error.status} / ${error.textStatus}`;
			details = $response.html();
		}
		else {
			title = 'Client js error';
			status = error.name;
			details = '<pre>' + mpro.Sanitize.escapeMarkup(error.stack) + '</pre>';
		}

		const $content = $(`<div class="request-error">
			<h4 class="request-error-title">${title} (${mpro.Sanitize.escapeMarkup(error.message)})</h4>
			<div class="request-error-status">${status}</div>
			<div class="request-error-url"><b>Request Url:</b> <i class="rqe_m">${initRequest?.method?.toUpperCase() || 'GET'} ➡ </i> <span class="rqe_ud">${initRequest.url}</span></div>
			<div class="request-error-body"><b>Request Body:</b> <i class="rqe_bd">${initRequest.data ? ((typeof initRequest.data === 'string') ? initRequest.data : JSON.stringify(initRequest.data)) : '<i class="rqe_e">(empty)</i>'}</i></div>
			<div class="request-error-response">${details}</div>
		</div>`);

		// Set iframe auto height
		$content.find('iframe[data-iframe-id="mp-error-iframe"]').on('load', function() {
			/** @type {HTMLIFrameElement} */ // @ts-ignore
			const that = this;
			$(that).height(that.contentDocument.scrollingElement.scrollHeight + 40);
		});

		return $content;
	}

	return getGenericErrorMarkup();
};

const stopNavigationHandler = (event) => {
	console.trace('Close the debug toast to enable the page navigation.');
	event.preventDefault();
	return (event.returnValue = 'Close the debug toast to enable the page navigation.');
};

/**
 * @param {JQuery<Partial<HTMLElement>>} $container
 * @return {JQuery<DocumentFragment>}
 */
export const removeContainerDevMessages = function removeContainerDevMessages($container) {

	/** @type {JQuery<HTMLTemplateElement>} */ // @ts-ignore
	const $msgFragment = $container.is('template[data-mp-dev-messages]') ? $container.filter('template[data-mp-dev-messages]') : $container.find('template[data-mp-dev-messages]');

	if ($msgFragment.length === 0) {
		return $();
	}

	// const fragment = $msgFragment[0].content.cloneNode(true);
	const fragment = $msgFragment[0].content;
	$msgFragment.remove();

	if (fragment.childElementCount === 0) {
		return $();
	}

	return $(fragment);
};

/**
 * @param {JQuery<Partial<HTMLElement>>} $container
 */
export const handleContainerDevMessages = function handleContainerDevMessages($container, url) {

	const $fragment = removeContainerDevMessages($container);

	if ($fragment.length > 0) {
		displayDevMessages($fragment.contents(), {
			title: `Current request debug/log messages ${url ? `(URL: ${url})` : ''}`,
			preventNavigation: false,
		});
	}

	return $fragment;
};

/**
 * Display extra content that came along with the partial (most likely debug messages or framework notices)
 *
 * @param {string | Node | JQuery<Text |Comment | Document | DocumentFragment> | JQuery<Partial<HTMLElement>>} content
 * @param {{ type?: import('../../modules/components/toast.js').MpToastOptions['format']; title?: string; preventNavigation?: boolean; }} options
 */
export const displayDevMessages = function displayDevMessages(content, options = {}) {

	// Do nothing if no content or not in dev mode
	if (!content || !mpro.isDev) {
		return;
	}

	/** @type {JQuery<Partial<HTMLElement>>} */ // @ts-ignore
	const $content = $(content);
	let { type: format, title, preventNavigation } = options;

	// Get type from comments
	if (!format) {

		// Get all comments
		const comments = $content.filter((_, v) => v instanceof Comment).get();
		if (comments.length > 0) {

			// If at least one error comment, then it is error
			if (comments.find(v => /\s*(ERR_ALERT)\s*/.test(v.textContent))) {
				format = 'warning';
			}
			// If no error comment, but at least one debug comment, then it is debug
			else if (comments.find(v => /\s*(MSG_DEBUG)\s*/.test(v.textContent))) {
				format = 'info';
			}
			// If no error or debug comment, then it is by default an error
			else {
				format = 'warning';
			}
		}
	}

	// Show extra content in a toast message
	const toast = mpro.toast(format, {
		type: 'debug',
		title,
		// @ts-ignore
		content: $content,
		icon: false,
		autohide: false,
		clearPrev: false,
		positionX: 'center',
		fullWidth: true,
		allowMinimize: true,
		// bodyClass: 'p-0',
	});

	// Remove unnecessary elements
	$content.filter('div.shm-debug-header').remove();

	// In case of a navigation, prevent it until the toast is closed
	if (preventNavigation) {

		// Prevent document from setting a location
		window.addEventListener('beforeunload', stopNavigationHandler, { capture: true });

		// Remove the beforeunload listener when the toast is closed
		$(toast.getElement()).one('hide.bs.toast', () => {
			window.removeEventListener('beforeunload', stopNavigationHandler, { capture: true });
		});
	}
};

/**
 * Production: display generic error message
 * Dev: display error message in a bigger toast
 *
 * @param {Error | any} error
 * @param {{ alertType?: import('../../modules/components/toast.js').MpToastOptions['format']; preventNavigation?: boolean; action: { type: string; method: string; url: string; } }} options
 */
export const displayEnvActionAlert = function displayEnvActionAlert(error, options) {

	const {
		alertType = 'error',
		preventNavigation,
		action,
	} = options;

	const $content = getErrorHtml({
		type: action.type,
		url: action.url,
		method: action.method,
		// @ts-ignore
	}, error instanceof Error ? error : new Error(error));

	// Display error message in a bigger toast if in dev mode
	if (mpro.isDev === true) {
		displayDevMessages($content, {
			type: alertType,
			title: `${mpro.upperFirst(mpro.camelize(action.type || 'Ajax'))} failed to load (${action.url})`,
			preventNavigation,
		});
	}
	else {

		// Display the error in a toast
		mpro.toast(alertType, {
			content: $content,
		});
	}
};

export default mpro;
